<template>
  <nav
    class="app-sidebar"
    :class="{
      'app-sidebar--mobile': mobile,
      'app-sidebar--open': modelValue,
      'app-sidebar--collapsed': !mobile && !modelValue,
    }"
  >
    <!-- Mobile Overlay (click to close sidebar) -->
    <!-- Using position: fixed with inset: 0 to cover entire viewport including header -->
    <button
      v-if="mobile && modelValue"
      class="app-sidebar__overlay"
      :class="{ 'app-sidebar__overlay--visible': modelValue }"
      type="button"
      @click.stop="$emit('update:modelValue', false)"
      @touchstart.stop.prevent="$emit('update:modelValue', false)"
      aria-label="Close sidebar"
    >
      <!-- Overlay content (required for Vue to not strip this element) -->
      <span class="sr-only">Tap to close sidebar</span>
    </button>
    <!-- Sidebar content -->
    <aside
      class="app-sidebar__content"
      :class="{ 'app-sidebar__content--visible': modelValue }"
    >
      <!-- Create button -->
      <div class="app-sidebar__create-buttons">
        <button class="app-sidebar__create-btn" @click="handlePrimaryCreate">
          <v-icon size="20">{{ primaryCreateIcon }}</v-icon>
          <span>{{ primaryCreateLabel }}</span>
        </button>
      </div>

      <!-- Primary Navigation (mode-specific) -->
      <div class="app-sidebar__nav">
        <router-link
          v-for="item in primaryNavItems"
          :key="item.to"
          :to="item.to"
          class="app-sidebar__nav-item"
          :class="{ 'app-sidebar__nav-item--active': isActive(item.to) }"
          @click="handleNavSelection(item.to)"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon
              :icon="
                isActive(item.to) ? item.iconActive || item.icon : item.icon
              "
              size="22"
            />
          </div>
          <span class="app-sidebar__nav-label">{{ item.title }}</span>
          <span v-if="item.badge" class="app-sidebar__nav-badge">{{
            item.badge
          }}</span>
        </router-link>

        <router-link
          to="/gunloot"
          class="app-sidebar__nav-item"
          :class="{ 'app-sidebar__nav-item--active': isGunLootActive }"
          @click="handleNavSelection('/gunloot')"
        >
          <div class="app-sidebar__nav-icon">
            <GunLootMenuIcon class="app-sidebar__gunloot-icon" />
          </div>
          <span class="app-sidebar__nav-label">GunLoot</span>
        </router-link>

        <router-link
          to="/discover"
          class="app-sidebar__nav-item"
          :class="{
            'app-sidebar__nav-item--active': isDiscoverActive,
          }"
          @click="handleNavSelection('/discover')"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon
              :icon="
                isDiscoverActive ? 'mdi-compass' : 'mdi-compass-outline'
              "
              size="22"
            />
          </div>
          <span class="app-sidebar__nav-label">Discover</span>
        </router-link>
      </div>

      <!-- Feed layout (platform — same pages that expose timeline-style feeds) -->
      <div
        v-if="isFeedPage && canToggleFeedMode"
        class="app-sidebar__nav"
      >
        <button
          type="button"
          class="app-sidebar__nav-item"
          @click="toggleFeedMode"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon
              :icon="
                feedViewMode === 'traditional'
                  ? 'mdi-view-sequential'
                  : 'mdi-view-grid'
              "
              size="22"
            />
          </div>
          <span class="app-sidebar__nav-label">{{
            feedViewMode === "traditional"
              ? "Single Post View"
              : "Traditional Feed"
          }}</span>
        </button>
      </div>

      <!-- Secondary Navigation (mode-specific) -->
      <div v-if="secondaryNavItems.length > 0" class="app-sidebar__nav">
        <router-link
          v-for="item in secondaryNavItems"
          :key="item.to"
          :to="item.to"
          class="app-sidebar__nav-item"
          :class="{ 'app-sidebar__nav-item--active': isActive(item.to) }"
          @click="handleNavSelection(item.to)"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon
              :icon="
                isActive(item.to) ? item.iconActive || item.icon : item.icon
              "
              size="22"
            />
          </div>
          <span class="app-sidebar__nav-label">{{ item.title }}</span>
          <span v-if="item.badge" class="app-sidebar__nav-badge">{{
            item.badge
          }}</span>
        </router-link>
      </div>

      <!-- Utility Navigation -->
      <div v-if="utilityNavItems.length > 0" class="app-sidebar__nav">
        <router-link
          v-for="item in utilityNavItems"
          :key="item.to"
          :to="item.to"
          class="app-sidebar__nav-item"
          :class="{
            'app-sidebar__nav-item--active': isActive(item.to),
          }"
          @click="handleNavSelection(item.to)"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon
              :icon="
                isActive(item.to) ? item.iconActive || item.icon : item.icon
              "
              size="22"
            />
          </div>
          <span class="app-sidebar__nav-label">{{ item.title }}</span>
          <span v-if="item.badge" class="app-sidebar__nav-badge">
            {{ item.badge }}
          </span>
        </router-link>

        <!-- Invite Friends (auth-gated, opens modal) -->
        <button
          v-if="isAuthenticated"
          class="app-sidebar__nav-item"
          @click="emit('open-invite'); closeSidebar()"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon icon="mdi-account-plus-outline" size="22" />
          </div>
          <span class="app-sidebar__nav-label">Invite Friends</span>
        </button>
      </div>

      <!-- Cross-app links -->
      <div v-if="directoryUrl" class="app-sidebar__nav">
        <a
          :href="directoryUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="app-sidebar__nav-item"
          @click="closeSidebar"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon icon="mdi-store-search-outline" size="22" />
          </div>
          <span class="app-sidebar__nav-label">Business Directory</span>
          <div class="app-sidebar__nav-icon" style="width: 16px; height: 16px;">
            <v-icon icon="mdi-open-in-new" size="14" />
          </div>
        </a>
      </div>

      <!-- Spacer -->
      <div class="app-sidebar__spacer" />

      <!-- Bottom section -->
      <div class="app-sidebar__bottom">
        <div class="app-sidebar__divider" />

        <!-- Website Link (if configured) -->
        <button
          v-if="siteWebsite"
          class="app-sidebar__nav-item"
          @click="openWebsite"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon icon="mdi-open-in-new" size="22" />
          </div>
          <span class="app-sidebar__nav-label">Website</span>
        </button>

        <router-link
          v-if="!isAuthenticated"
          to="/login"
          class="app-sidebar__nav-item app-sidebar__nav-item--highlight"
          @click="closeSidebar"
        >
          <div class="app-sidebar__nav-icon">
            <v-icon icon="mdi-login" size="22" />
          </div>
          <span class="app-sidebar__nav-label">Sign In</span>
        </router-link>

        <!-- User shortcut (mobile drawer): opens profile; account actions live in header menu -->
        <router-link
          v-if="isAuthenticated && user"
          :to="profileLink"
          class="app-sidebar__user-card"
          @click="closeSidebar"
        >
          <div class="app-sidebar__user-avatar">
            <v-img
              v-if="userAvatar"
              :src="userAvatar"
              :alt="user.name"
              cover
            />
            <span v-else>{{
              user.name?.charAt(0).toUpperCase() || "U"
            }}</span>
          </div>
          <div class="app-sidebar__user-info">
            <span class="app-sidebar__user-name">{{
              user.name || "User"
            }}</span>
            <span class="app-sidebar__user-handle"
              >@{{ user.username || "unknown" }}</span
            >
          </div>
        </router-link>

        <!-- Footer Links -->
        <nav class="app-sidebar__footer-links">
          <a href="#" @click.prevent="handleOpenTerms">Terms</a>
          <span class="app-sidebar__footer-dot">·</span>
          <a href="#" @click.prevent="handleOpenPrivacy">Privacy</a>
          <span class="app-sidebar__footer-dot">·</span>
          <router-link to="/help" @click="closeSidebar">Help</router-link>
        </nav>

        <!-- App Version -->
        <div class="app-sidebar__version">
          <span>&copy; Closed Circuit Inc.</span>
          <span class="app-sidebar__version-number">{{ appVersion }}</span>
        </div>
      </div>
    </aside>
  </nav>

</template>

<script setup lang="ts">
import { computed, inject, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useAuth } from "@/composables/useAuth";
import { useAppSettings } from "@/composables/useAppSettings";
import { useUiStore } from "@/stores/ui";
import { useChatGroups } from "@/queries/useMessagesQueries";
import { useCreatePostContext } from "@/composables/useCreatePostContext";
import GunLootMenuIcon from "@/components/icons/GunLootMenuIcon.vue";
import type { OpenCreatePostOptions } from "@/types";

// Inject create functions from App.vue
const openCreatePost = inject<
  ((options?: OpenCreatePostOptions) => void) | undefined
>("openCreatePost", undefined);
const { getGroupContext } = useCreatePostContext();

interface Props {
  modelValue?: boolean;
  rail?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: true,
  rail: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  "open-settings": [];
  "open-terms": [];
  "open-privacy": [];
  "open-discover": [];
  "open-invite": [];
}>();

const route = useRoute();
const { mobile } = useDisplay();
const { user, isAuthenticated } = useAuth();
const { siteWebsite, messagingEnabled } = useAppSettings();
const uiStore = useUiStore();

const feedViewMode = computed(() => uiStore.feedViewMode);
const canToggleFeedMode = computed(() => uiStore.canToggleFeedMode);

const isDiscoverActive = computed(
  () =>
    route.name === "discover" || route.name === "discover-category",
);

const isGunLootActive = computed(() => route.name === "gunloot");

const isFeedPage = computed(() => {
  const feedRoutes = ["/", "/explore", "/trending", "/following"];
  const feedRouteNames = [
    "home",
    "explore",
    "trending",
    "following",
    "timeline",
  ];
  return (
    feedRoutes.includes(route.path) ||
    feedRouteNames.includes(route.name as string) ||
    route.path.startsWith("/timeline")
  );
});

// Auto-close sidebar on mobile when route changes
watch(
  () => route.path,
  () => {
    if (mobile.value && props.modelValue) {
      emit("update:modelValue", false);
    }
  },
);

// App version from environment variable
const appVersion = computed(() => {
  return import.meta.env.VITE_APP_VERSION || "0.0.1";
});

// Cross-app links
// Default: swap "app." -> "directory." on the current hostname.
// VITE_DIRECTORY_URL overrides for staging/preview hosts.
function deriveDirectoryUrl(): string {
  const override = (import.meta.env.VITE_DIRECTORY_URL || "").trim();
  if (override) {
    return /^https?:\/\//i.test(override) ? override : "";
  }
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  if (!host.startsWith("app.")) return "";
  return `${window.location.protocol}//directory.${host.slice(4)}`;
}
const directoryUrl = deriveDirectoryUrl();

// Chat groups for unread count - updates are received via real-time WebSocket events
const { groups: chatGroups } = useChatGroups({
  enabled: computed(() => isAuthenticated.value && messagingEnabled.value),
});

// Track unread message count and pulse animation
const totalUnreadMessages = computed(() => {
  return chatGroups.value.reduce(
    (total, group) => total + (group.unreadCount || 0),
    0,
  );
});

// Pulse animation state - triggers when new message arrives
const hasNewMessage = ref(false);
let pulseTimeout: ReturnType<typeof setTimeout> | null = null;

watch(totalUnreadMessages, (newCount, oldCount) => {
  if (newCount > oldCount) {
    // New message arrived - trigger pulse
    hasNewMessage.value = true;
    if (pulseTimeout) clearTimeout(pulseTimeout);
    pulseTimeout = setTimeout(() => {
      hasNewMessage.value = false;
    }, 2000); // Pulse for 2 seconds
  }
});

interface NavItem {
  to: string;
  icon: string;
  iconActive?: string;
  title: string;
  badge?: number;
}

// Tab persistence: Store last visited discover category (excluding timeline which has its own menu item)
const lastDiscoverTab = ref<string>("songs");

// Watch route changes to remember discover tab
watch(
  () => route.path,
  (newPath) => {
    if (newPath.startsWith("/discover/")) {
      const category = newPath.split("/")[2];
      // Exclude timeline since it has its own menu item
      if (category && ["trending", "songs"].includes(category)) {
        lastDiscoverTab.value = category;
      }
    }
  },
  { immediate: true },
);

// Unified primary navigation
const primaryNavItems = computed<NavItem[]>(() => {
  return [
    {
      to: "/timeline",
      icon: "mdi-timeline-text-outline",
      iconActive: "mdi-timeline-text",
      title: "Timeline",
    },
    { to: "/search", icon: "mdi-magnify", title: "Search" },
  ];
});

// Secondary navigation (empty — music features removed)
const secondaryNavItems = computed<NavItem[]>(() => []);

// Utility navigation items (shown to all users)
const utilityNavItems = computed<NavItem[]>(() => {
  const items: NavItem[] = [
    {
      to: "/communities",
      icon: "mdi-account-group-outline",
      iconActive: "mdi-account-group",
      title: "Communities",
    },
  ];

  if (isAuthenticated.value) {
    items.push({
      to: "/settings/feedback",
      icon: "mdi-comment-question-outline",
      iconActive: "mdi-comment-question",
      title: "Feedback",
    });
  }

  return items;
});

// Create button
const primaryCreateIcon = computed(() => "mdi-plus");
const primaryCreateLabel = computed(() => "Post");

const profileLink = computed(() => {
  return user.value ? `/profile/${user.value.username}` : "/profile";
});

// Computed avatar that handles different field names from API
const userAvatar = computed(() => {
  if (!user.value) return null;
  const u = user.value as Record<string, unknown>;
  return (u.avatar || u.avatarUrl || u.avatar_url || u.profile_image) as
    | string
    | null;
});

function isActive(path: string): boolean {
  if (path === "/") {
    return route.path === "/";
  }
  return route.path.startsWith(path);
}

function closeSidebar() {
  if (mobile.value) {
    emit("update:modelValue", false);
  }
}

function handleOpenTerms() {
  closeSidebar();
  emit("open-terms");
}

function handleOpenPrivacy() {
  closeSidebar();
  emit("open-privacy");
}

function toggleFeedMode() {
  const newMode =
    feedViewMode.value === "traditional" ? "single-post" : "traditional";
  uiStore.setFeedViewMode(newMode);
  closeSidebar();
}

function handlePrimaryCreate() {
  openCreatePost?.(getGroupContext());
  closeSidebar();
}

// When clicking a nav item that's already active, scroll feed to top.
// Always close the mobile drawer so the fixed overlay never blocks the new route.
function handleNavSelection(to: string) {
  if (isActive(to)) {
    const singlePostFeed = document.querySelector('.single-post-feed') as HTMLElement;
    if (singlePostFeed && singlePostFeed.scrollTop > 0) {
      singlePostFeed.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  closeSidebar();
}

function openWebsite() {
  if (siteWebsite.value) {
    window.open(siteWebsite.value, "_blank", "noopener,noreferrer");
  }
}

// Clean up pulse timeout on unmount
onUnmounted(() => {
  if (pulseTimeout) {
    clearTimeout(pulseTimeout);
    pulseTimeout = null;
  }
});
</script>

<style scoped lang="scss">
.app-sidebar {
  // Define header height including safe area for consistency
  --header-height: calc(64px + env(safe-area-inset-top, 0px));

  position: fixed;
  top: var(--header-height); // Account for header + notch/Dynamic Island
  left: 0;
  bottom: 0;
  z-index: var(--z-sidebar);
  width: 260px;

  // Align sidebar with the centered container whenever the container is centered
  // Match the header breakpoint at 1360px - when viewport >= 1360px, container centers
  // app-max-width = 260px (sidebar) + 1100px (content) = 1360px
  @media (min-width: 1360px) {
    // Calculate left offset to align with container's left edge
    // When container is centered: left margin = (100vw - app-max-width) / 2
    // Sidebar should align with container's left edge, so use the same calculation
    left: calc((100vw - var(--app-max-width)) / 2);
  }

  // Desktop collapsed state - show only icons
  // In single-post mode, always show full sidebar
  &--collapsed {
    width: var(--sidebar-collapsed-width, 72px);
    transition: width 0.3s var(--ease-out-expo);

    .app-wrapper--single-post & {
      width: 260px;
    }

    .app-sidebar__content {
      width: var(--sidebar-collapsed-width, 72px);
      padding: 16px 8px;

      .app-wrapper--single-post & {
        width: 260px;
        padding: 20px 16px;
      }
      transform: translateX(0);
      transition:
        width 0.3s var(--ease-out-expo),
        padding 0.3s var(--ease-out-expo);
    }

    // Hide labels, badges, and section titles in collapsed mode
    .app-sidebar__nav-label,
    .app-sidebar__nav-badge,
    .app-sidebar__section-title,
    .app-sidebar__create-btn span,
    .app-sidebar__upload-btn span,
    .app-sidebar__user-info,
    .app-sidebar__footer-links,
    .app-sidebar__version {
      opacity: 0;
      width: 0;
      overflow: hidden;
      transition:
        opacity 0.2s ease,
        width 0.2s ease;
    }

    // Center icons in nav items
    .app-sidebar__nav-item {
      justify-content: center;
      padding: 12px;
      gap: 0;
    }

    // Make create buttons icon-only circles
    .app-sidebar__create-btn,
    .app-sidebar__upload-btn {
      width: 48px;
      height: 48px;
      padding: 0;
      border-radius: 50%;
      justify-content: center;
      gap: 0;
    }

    .app-sidebar__create-buttons {
      align-items: center;
    }

    // Collapse user card to just avatar
    .app-sidebar__user-card {
      justify-content: center;
      padding: 8px;
    }

    // Hide section dividers in collapsed mode
    .app-sidebar__section {
      border-top: none;
      padding-top: 8px;
      margin-top: 8px;
    }

    // Hide bottom section divider
    .app-sidebar__divider {
      display: none;
    }

    // Hide spacer (not needed in collapsed)
    .app-sidebar__spacer {
      min-height: 8px;
    }
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  // Desktop expanded state
  &:not(&--mobile)#{&}--open {
    .app-sidebar__content {
      transform: translateX(0);
      transition: transform 0.3s var(--ease-out-expo);
    }
  }

  // iOS PWA / standalone mode - ensure proper safe area handling
  @supports (padding-top: env(safe-area-inset-top)) {
    @media (display-mode: standalone) {
      --header-height: calc(64px + env(safe-area-inset-top, 47px));
    }
  }

  &--mobile {
    // On mobile, sidebar starts off-screen and slides in when opened
    // Position above everything including header
    top: 0;
    z-index: var(--z-mobile-menu);
    width: 280px; // Fixed width, not 100% - overlay handles the rest
    // CRITICAL: Don't capture pointer events on the nav wrapper when closed
    pointer-events: none;

    .app-sidebar__content {
      // Account for safe area at top on mobile
      padding-top: calc(20px + env(safe-area-inset-top, 0px));
      pointer-events: auto; // Re-enable pointer events on content
    }

    // When open, allow pointer events on the whole sidebar area
    &.app-sidebar--open {
      pointer-events: auto;
    }
  }

  // Mode Switcher - Premium glass morphism design
  &__mode-switcher {
    margin-bottom: 20px;
  }

  &__mode-track {
    position: relative;
    display: flex;
    padding: 4px;
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-on-surface), 0.03) 0%,
      rgba(255, 255, 255, 0.01) 100%
    );
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 14px;
    backdrop-filter: blur(8px);
    touch-action: none; // Disable browser touch handling to capture all touch events
    user-select: none; // Prevent text selection during swipe
    -webkit-user-select: none;
    -webkit-touch-callout: none; // Disable iOS callout
  }

  &__mode-indicator {
    position: absolute;
    top: 4px;
    height: 36px;
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-primary), 0.2) 0%,
      rgba(var(--v-theme-primary), 0.12) 100%
    );
    border: 1px solid rgba(var(--v-theme-primary), 0.25);
    border-radius: 10px;
    box-shadow:
      0 4px 12px rgba(var(--v-theme-primary), 0.15),
      inset 0 1px 0 rgba(var(--v-theme-on-surface), 0.1);
    transition:
      left 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
      width 0.35s ease;
    pointer-events: none;
    z-index: var(--z-base);
  }

  &__mode-btn {
    position: relative;
    z-index: var(--z-base-overlay);
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 68px;
    height: 36px;
    border: none;
    background: transparent;
    border-radius: 10px;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    transition: all 0.25s ease;

    .v-icon {
      transition:
        transform 0.25s ease,
        color 0.25s ease;
    }

    &:hover:not(&--active) {
      color: rgba(var(--v-theme-on-surface), 0.75);

      .v-icon {
        transform: scale(1.1);
      }
    }

    &--active {
      color: rgb(var(--v-theme-primary));

      .v-icon {
        transform: scale(1.15);
        filter: drop-shadow(0 2px 4px rgba(var(--v-theme-primary), 0.4));
      }
    }
  }

  // Overlay (matches search modal overlay pattern)
  &__overlay {
    position: fixed;
    top: 0;
    left: 280px; // Start after sidebar (sidebar width on mobile)
    right: 0;
    bottom: 0;
    border: 0;
    padding: 0;
    background: rgba(var(--v-theme-overlay), 0.7);
    backdrop-filter: blur(4px);
    opacity: 0;
    z-index: var(--z-sidebar-overlay);
    transition: opacity 0.3s var(--ease-out-expo);
    pointer-events: none;
    cursor: pointer;
    appearance: none;
    touch-action: none; // Prevent any default touch behavior

    // Only show on mobile (< 1280px)
    @media (min-width: 1280px) {
      display: none !important;
    }

    &--visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  // Content
  &__content {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 20px 16px;
    padding-bottom: 120px; // Extra padding for audio player
    background: rgba(12, 10, 9, 0.5);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-right: 1px solid rgba(255, 255, 255, 0.06);
    overflow-y: auto;
    pointer-events: auto;
    position: relative;
    z-index: var(--z-sidebar-drawer);

    // On desktop, sidebar is always visible (controlled by v-model in App.vue)
    // On mobile, sidebar slides in from the left
    .app-sidebar--mobile & {
      width: 280px;
      box-shadow: 4px 0 24px rgba(0, 0, 0, 0.3);
      transform: translateX(-100%);
      transition: transform 0.3s var(--ease-out-expo);
    }

    .app-sidebar--mobile &--visible {
      transform: translateX(0);
    }
  }

  // Section
  &__section {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  &__section-title {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(var(--v-theme-on-surface), 0.5);
    padding: 0 16px 8px;
    margin: 0;
  }

  // Navigation
  &__nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__nav-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border: none;
    background: none;
    border-radius: 14px;
    text-decoration: none;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    transition: all 0.2s var(--ease-out-expo);
    width: 100%;
    text-align: left;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
      color: rgb(var(--v-theme-on-surface));
    }

    &--active {
      background: rgba(var(--v-theme-primary), 0.1);
      color: rgb(var(--v-theme-on-surface));

      .app-sidebar__nav-icon {
        color: rgb(var(--v-theme-primary));
      }

      &:hover {
        background: rgba(var(--v-theme-primary), 0.15);
      }
    }

    &--danger {
      color: rgba(var(--v-theme-on-surface), 0.6);

      &:hover {
        background: rgba(var(--v-theme-error), 0.1);
        color: rgb(var(--v-theme-error));

        .app-sidebar__nav-icon {
          color: rgb(var(--v-theme-error));
        }
      }
    }

    &--highlight {
      background: linear-gradient(
        135deg,
        rgba(var(--v-theme-primary), 0.15),
        rgba(var(--v-theme-primary), 0.05)
      );
      color: rgb(var(--v-theme-primary));

      &:hover {
        background: linear-gradient(
          135deg,
          rgba(var(--v-theme-primary), 0.2),
          rgba(var(--v-theme-primary), 0.1)
        );
      }
    }
  }

  &__nav-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: inherit;
  }

  &__gunloot-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  &__nav-label {
    flex: 1;
  }

  &__nav-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    background: rgb(var(--v-theme-primary));
    border-radius: 50px;
    font-size: 0.6875rem;
    font-weight: 700;
    color: rgb(var(--v-theme-on-primary, 12 10 9));
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;

    &--pulse {
      animation: badge-pulse 0.6s ease-in-out infinite;
      box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.7);
    }
  }

  // Pulse animation for new messages
  &__nav-item--pulse {
    .app-sidebar__nav-icon {
      animation: icon-shake 0.5s ease-in-out;
    }
  }

  @keyframes badge-pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.7);
    }
    50% {
      transform: scale(1.15);
      box-shadow: 0 0 0 8px rgba(var(--v-theme-primary), 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0);
    }
  }

  @keyframes icon-shake {
    0%,
    100% {
      transform: translateX(0) rotate(0);
    }
    20% {
      transform: translateX(-2px) rotate(-5deg);
    }
    40% {
      transform: translateX(2px) rotate(5deg);
    }
    60% {
      transform: translateX(-2px) rotate(-3deg);
    }
    80% {
      transform: translateX(2px) rotate(3deg);
    }
  }

  // Create buttons container
  &__create-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 16px;
    margin-bottom: 20px;
  }

  // Create button
  &__create-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 48px;
    border: none;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)) 0%,
      rgb(var(--v-theme-secondary)) 100%
    );
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-primary, 12 10 9));
    cursor: pointer;
    transition: all 0.3s var(--ease-out-expo);
    box-shadow: 0 4px 16px rgba(var(--v-theme-primary), 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(var(--v-theme-primary), 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  }

  // Upload music button
  &__upload-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 44px;
    border: 1px solid rgba(var(--v-theme-primary), 0.3);
    background: rgba(var(--v-theme-primary), 0.08);
    border-radius: 50px;
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    transition: all 0.3s var(--ease-out-expo);

    &:hover {
      background: rgba(var(--v-theme-primary), 0.15);
      border-color: rgba(var(--v-theme-primary), 0.5);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  // Spacer
  &__spacer {
    flex: 1;
    min-height: 24px;
  }

  // Bottom section
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__divider {
    height: 1px;
    margin: 8px 0 12px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(var(--v-theme-on-surface), 0.08),
      transparent
    );
  }

  // User card (hidden on desktop, shown on mobile only) — profile only; account menu is in header
  &__user-card {
    display: none;
    align-items: center;
    gap: 12px;
    padding: 12px;
    margin-top: 12px;
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
    color: inherit;

    // Show on mobile
    .app-sidebar--mobile & {
      display: flex;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.06);
    }
  }

  &__user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary))
    );
    padding: 2px;
    flex-shrink: 0;

    .v-img,
    span {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: rgba(var(--v-theme-on-surface), 0.08);
      font-size: 0.875rem;
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__user-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__user-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__user-handle {
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Footer links
  &__footer-links {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    padding: 8px 16px;

    a {
      font-size: 0.8125rem;
      color: rgba(var(--v-theme-on-surface), 0.5);
      text-decoration: none;
      transition: color 0.2s ease;

      &:hover {
        color: rgb(var(--v-theme-on-surface));
        text-decoration: underline;
      }
    }
  }

  &__footer-dot {
    color: rgba(var(--v-theme-on-surface), 0.3);
  }

  // Version display
  &__version {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 16px;
    margin-top: 4px;
    font-size: 0.6875rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
  }

  &__version-number {
    font-family: var(--font-mono, monospace);
    font-size: 0.625rem;
    color: rgba(var(--v-theme-on-surface), 0.4);
  }
}

// Light theme
.v-theme--light {
  .app-sidebar {
    &__content {
      background: rgba(250, 250, 249, 0.6);
      border-right-color: rgba(0, 0, 0, 0.06);
    }

    &__mode-track {
      background: linear-gradient(
        135deg,
        rgba(var(--v-theme-on-surface), 0.03) 0%,
        rgba(0, 0, 0, 0.01) 100%
      );
      border-color: rgba(0, 0, 0, 0.06);
    }

    &__mode-indicator {
      background: linear-gradient(
        135deg,
        rgba(var(--v-theme-primary), 0.15) 0%,
        rgba(var(--v-theme-primary), 0.08) 100%
      );
      border-color: rgba(var(--v-theme-primary), 0.2);
      box-shadow:
        0 4px 12px rgba(var(--v-theme-primary), 0.1),
        inset 0 1px 0 rgba(var(--v-theme-on-surface), 0.5);
    }

    &__mode-btn {
      color: rgba(var(--v-theme-on-surface), 0.55);

      &:hover:not(.app-sidebar__mode-btn--active) {
        color: rgba(var(--v-theme-on-surface), 0.7);
      }

      &--active {
        color: rgb(var(--v-theme-primary));

        .v-icon {
          filter: drop-shadow(0 2px 4px rgba(var(--v-theme-primary), 0.3));
        }
      }
    }

    &__nav-item {
      color: rgba(var(--v-theme-on-surface), 0.4);

      &:hover {
        background: rgba(0, 0, 0, 0.04);
        color: rgb(var(--v-theme-on-surface));
      }

      &--active {
        background: rgba(var(--v-theme-primary), 0.08);
        color: rgb(var(--v-theme-on-surface));

        &:hover {
          background: rgba(var(--v-theme-primary), 0.12);
        }
      }

      &--highlight {
        background: linear-gradient(
          135deg,
          rgba(var(--v-theme-primary), 0.12),
          rgba(var(--v-theme-primary), 0.04)
        );
        color: rgb(var(--v-theme-primary));
      }
    }

    &__section {
      border-top-color: rgba(0, 0, 0, 0.06);
    }

    &__section-title {
      color: rgba(var(--v-theme-on-surface), 0.6);
    }

    &__upload-btn {
      border-color: rgba(var(--v-theme-primary), 0.3);
      background: rgba(var(--v-theme-primary), 0.08);
      color: rgb(var(--v-theme-primary));

      &:hover {
        background: rgba(var(--v-theme-primary), 0.15);
        border-color: rgba(var(--v-theme-primary), 0.5);
      }
    }

    &__divider {
      background: linear-gradient(
        90deg,
        transparent,
        rgba(var(--v-theme-on-surface), 0.08),
        transparent
      );
    }

    &__user-card {
      background: rgba(var(--v-theme-on-surface), 0.03);

      &:hover {
        background: rgba(var(--v-theme-on-surface), 0.05);
      }
    }

    &__user-avatar {
      span {
        background: rgba(var(--v-theme-on-surface), 0.04);
        color: rgb(var(--v-theme-on-surface));
      }
    }

    &__user-name {
      color: rgb(var(--v-theme-on-surface));
    }

    &__menu-item {
      color: rgb(var(--v-theme-on-surface));

      .v-icon {
        color: inherit;
      }

      &:hover {
        background: rgba(0, 0, 0, 0.04);
        color: rgb(var(--v-theme-on-surface));
      }
    }
  }
}
</style>
